import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import SwButton from "../SwButtons/SwButtons";

import UploadDeleteIcon from "./img/upload-del-icon.svg";
import iconPdf from "./img/icon-pdf.png";
import iconVideo from "./img/icon-video.png";
import iconDoc from "./img/icon-doc.png";
import iconXls from "./img/icon-xls.png";
import iconCsv from "./img/icon-csv.png";
import iconPpt from "./img/icon-ppt.png";
import iconHeic from "./img/icon-heic.png";

const SwFileUploader = ({
  /* ---------- Behavior ---------- */
  mode = "both", // "dropzone" | "button" | "both"
  multiple = true,
  disabled = false,

  /* ---------- Limits ---------- */
  maxFiles,
  maxSizeMB,

  /* ---------- UI ---------- */
  helperText,
  showUploadButton = true,
  showFileCount = true,
  previewPlacement = "bottom", // "top" | "bottom"

  /* ---------- Button ---------- */
  buttonText = "Click to Upload",
  buttonVariant = "dark",

  /* ---------- Callbacks ---------- */
  onChange,

  /* ---------- Dropzone ---------- */
  accept,
  className = "",
}) => {
  const [files, setFiles] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");

  const maxSizeBytes = maxSizeMB ? maxSizeMB * 1024 * 1024 : undefined;

  /* ---------- Helpers ---------- */

  const getFileIcon = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();

    if (file.type.startsWith("image/") && ext !== "heic") {
      return file.preview;
    }

    switch (ext) {
      case "heic": return iconHeic;
      case "pdf": return iconPdf;
      case "mp4":
      case "avi":
      case "mov":
      case "mkv": return iconVideo;
      case "doc":
      case "docx":
      case "txt": return iconDoc;
      case "xls":
      case "xlsx": return iconXls;
      case "csv": return iconCsv;
      case "ppt":
      case "pptx": return iconPpt;
      default: return UploadDeleteIcon;
    }
  };

  const trimFileName = (name) => {
    const max = 30;
    const ext = name.split(".").pop();
    const base = name.slice(0, -(ext.length + 1));
    return base.length > max ? `${base.slice(0, max)}... .${ext}` : name;
  };

  /* ---------- Drop Handling ---------- */

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      let error = "";

      if (rejectedFiles.length) {
        const { code } = rejectedFiles[0].errors[0];
        if (code === "file-too-large")
          error = `File exceeds ${maxSizeMB} MB limit.`;
        if (code === "file-invalid-type")
          error = "Unsupported file type.";
        if (code === "too-many-files")
          error = `Maximum ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed.`;
      }

      const uniqueNewFiles = [];

      acceptedFiles.forEach((file) => {
        if (files.some((f) => f.name === file.name)) {
          error = `${file.name} is already uploaded.`;
        } else {
          uniqueNewFiles.push(
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            })
          );
        }
      });

      if (
        maxFiles &&
        files.length + uniqueNewFiles.length > maxFiles
      ) {
        error = `You can upload a maximum of ${maxFiles} file${
          maxFiles > 1 ? "s" : ""
        }.`;
      }

      if (error) {
        setErrorMsg(error);
        return;
      }

      setErrorMsg("");
      setFiles((prev) => {
        const updated = [...prev, ...uniqueNewFiles];
        onChange?.(updated);
        return updated;
      });
    },
    [files, maxFiles, maxSizeMB, onChange]
  );

  const deleteFile = (file) => {
    setFiles((prev) => {
      const updated = prev.filter((f) => f !== file);
      onChange?.(updated);
      return updated;
    });
    URL.revokeObjectURL(file.preview);
  };

  /* ---------- Dropzone ---------- */

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept,
    multiple,
    maxFiles,
    maxSize: maxSizeBytes,
    disabled,
    noClick: mode === "button",
  });

  /* ---------- UI Blocks ---------- */

  const PreviewUI = (
    <div className="UploadPreview">
      {files.map((file) => (
        <div key={file.name} className="preview-UploadItem">
          {file.type.startsWith("image/") &&
          file.name.split(".").pop().toLowerCase() !== "heic" ? (
            <img
              src={getFileIcon(file)}
              alt={file.name}
              className="preview-UploadImage"
            />
          ) : (
            <div className="UploadFileDetail">
              <img src={getFileIcon(file)} alt={file.name} />
              <div className="UploadFileName">
                {trimFileName(file.name)}
              </div>
            </div>
          )}

          <div className="preview-UploadOverlay">
            {file.type.startsWith("image/") && (
              <span className="UploadFileName text-light">
                {file.name}
              </span>
            )}
            {!disabled && (
              <img
                src={UploadDeleteIcon}
                alt="Delete"
                className="delete-icon"
                onClick={() => deleteFile(file)}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );

  /* ---------- Render ---------- */

  return (
    <div className={`SwDropzone ${className}`} aria-disabled={disabled}>

      {/* Hidden input — REQUIRED for button mode */}
      <input {...getInputProps()} />

      {previewPlacement === "top" && PreviewUI}

      {/* Dropzone UI */}
      {mode !== "button" && (
        <div
          {...getRootProps({
            className: "dropzone",
            role: "button",
            tabIndex: disabled ? -1 : 0,
          })}
        >
          <div>Drag & drop files here, or click to upload</div>
        </div>
      )}

      {/* Helper / Error */}
      {errorMsg ? (
        <div className="error-message" aria-live="polite">
          {errorMsg}
        </div>
      ) : (
        helperText && <div className="SwHelperText">{helperText}</div>
      )}

      {/* File Count */}
      {showFileCount && maxFiles && (
        <div className="UploadCount">
          {files.length} / {maxFiles} files uploaded
        </div>
      )}

      {/* Upload Button */}
      {showUploadButton && (
        <SwButton
          variant={buttonVariant}
          size="sm"
          disabled={disabled}
          onClick={open}
          className="mt-3"
        >
          {buttonText}
        </SwButton>
      )}

      {previewPlacement === "bottom" && PreviewUI}
    </div>
  );
};

export default SwFileUploader;
